import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CONTACT, PERSONALIZED_PRODUCTS, CATALOG_OCCASIONS } from "@/lib/products";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs, { breadcrumbSchema } from "@/components/site/Breadcrumbs";
import { trackWhatsAppClick } from "@/lib/analytics";

// Per-category SEO copy shown when the Catalog page is filtered via ?type=X.
const CATEGORY_SEO = {
  coin: {
    label: "Silver Coins",
    description:
      "Shop our full range of hand-engraved silver photo coins, 5g to 100g, in round & square acrylic frames — personalized with your photo. Order on WhatsApp.",
  },
  locket: {
    label: "Gold Lockets",
    description:
      "Explore our 22ct gold engraved photo lockets — wearable pendants personalized with your photograph, handcrafted in Hyderabad. Enquire on WhatsApp today.",
  },
  bracelet: {
    label: "Bracelets",
    description:
      "Shop our designer stretchable gold nameplate bracelets, customized with names, initials or symbols. Daily-wear 18ct gold. Order on WhatsApp for pricing.",
  },
  plate: {
    label: "Plate Mementos",
    description:
      "Browse our 100g silver plate mementos — engraved keepsakes for retirements, anniversaries & corporate gifting, personalized to your design. WhatsApp us.",
  },
  momento: {
    label: "Multi-Coin Momentos",
    description:
      "Discover our arched multi-coin momento, a premium personalized silver keepsake holding multiple engraved photos in one elegant frame. Order on WhatsApp.",
  },
};
const DEFAULT_CATALOG_DESCRIPTION =
  "Browse our full catalog of hand-engraved gold & silver keepsakes — photo coins, lockets, plate mementos and bracelets. Contact us on WhatsApp for pricing.";

// `standalone` is true only on the dedicated /catalog route (CatalogPage) —
// it renders the page's <Seo>, a single <h1>, and breadcrumb nav. When this
// component is embedded on the homepage (Section 02), none of that renders
// since the homepage already owns its own <h1>/<Seo>.
export default function CatalogSection({ standalone = false }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const typeFilter = searchParams.get("type");
  const occasionSlug = searchParams.get("occasion");
  const occasion = useMemo(
    () => (occasionSlug ? CATALOG_OCCASIONS.find((o) => o.slug === occasionSlug) : null),
    [occasionSlug]
  );

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/products");
        setProducts(res.data || []);
      } catch (e) {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // If the admin has not yet published any ready-made pieces, gracefully fall back to
  // the live personalized-engraving catalog so buyers still see the full range of
  // designs — every card links to the "View All Similar" gallery for that design.
  const fallback = PERSONALIZED_PRODUCTS.map((p) => ({
    id: p.id,
    name: p.name,
    category: p.productTypeLabel,
    metal: p.productType === "locket" || p.productType === "bracelet" ? "gold" : "silver",
    description: p.blurb,
    image_url: p.image,
    price_hint: "Contact for Price",
    _samplesHref: `/samples/${p.productType}/${p.designType}/${p.weights?.[0] || "multi"}`,
    _designLabel: p.designLabel,
    _productType: p.productType,
  }));

  const filteredFallback = useMemo(() => {
    let out = fallback;
    if (typeFilter) out = out.filter((p) => p._productType === typeFilter);
    if (occasion) out = out.filter((p) => occasion.productTypes.includes(p._productType));
    return out;
  }, [fallback, typeFilter, occasion]);
  const categoryLabel = useMemo(
    () => fallback.find((p) => p._productType === typeFilter)?.category,
    [fallback, typeFilter]
  );
  const seoInfo = typeFilter ? CATEGORY_SEO[typeFilter] : null;
  // Occasion labels like "Wedding Gifts" already end in "Gifts" — strip that
  // suffix before appending "Gift Ideas"/"Gifts" elsewhere, so headings read
  // "Wedding Gift Ideas" instead of "Wedding Gifts Gift Ideas".
  const occasionShortLabel = occasion ? occasion.label.replace(/\s+Gifts?$/i, "") : null;
  const breadcrumbItems = occasion
    ? [
        { label: "Home", href: "/" },
        { label: "Catalog", href: "/catalog" },
        { label: `${occasionShortLabel} Gifts`, href: `/catalog?occasion=${occasion.slug}` },
      ]
    : typeFilter
    ? [
        { label: "Home", href: "/" },
        { label: "Catalog", href: "/catalog" },
        { label: seoInfo?.label || categoryLabel || "Category", href: `/catalog?type=${typeFilter}` },
      ]
    : [{ label: "Home", href: "/" }, { label: "Catalog", href: "/catalog" }];

  // For real admin-added products (which use generic categories like "Rings"/
  // "Necklaces" — a separate legacy taxonomy), we can't reliably match them
  // against an occasion's productTypes, so an occasion filter only narrows
  // the fallback personalized-engraving list; real catalog items always show.
  const list = products.length ? products : filteredFallback;
  const usingFallback = products.length === 0 && !loading;

  const headingContent = occasion ? (
    <>{occasionShortLabel} <span className="gold-gradient-text italic">Gift Ideas</span></>
  ) : typeFilter ? (
    <>{categoryLabel || "Collection"} <span className="gold-gradient-text italic">Catalog</span></>
  ) : (
    <>Ready-made <span className="gold-gradient-text italic">gold & silver</span></>
  );

  return (
    <section id="catalog" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        {standalone && (
          <>
            <Seo
              title={occasion ? `${occasionShortLabel} Gift Ideas` : seoInfo ? `${seoInfo.label} Catalog` : "Catalog — Ready-Made Gold & Silver Keepsakes"}
              description={occasion?.description || seoInfo?.description || DEFAULT_CATALOG_DESCRIPTION}
              path="/catalog"
              jsonLd={[breadcrumbSchema(breadcrumbItems)]}
            />
            <div className="mb-8">
              <Breadcrumbs items={breadcrumbItems} testId="catalog-breadcrumbs" />
            </div>
          </>
        )}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            {!standalone && <div className="text-xs tracking-widest-2 uppercase text-[#d4af37]">Section 03 · Catalog</div>}
            {standalone ? (
              <h1 className="mt-4 font-display text-4xl md:text-6xl leading-[1.05] text-white">{headingContent}</h1>
            ) : (
              <h2 className="mt-4 font-display text-4xl md:text-6xl leading-[1.05] text-white">{headingContent}</h2>
            )}
            <p className="mt-4 text-white/70 md:text-lg">
              A curated selection of our latest pieces. Every design is available for made-to-order finishing —
              tap any piece to explore every real engraving example or enquire on WhatsApp.
            </p>
            {(typeFilter || occasion) && (
              <Link
                to="/catalog"
                className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest-2 text-[#d4af37] hover:underline"
                data-testid="catalog-clear-filter"
              >
                ← View Full Catalog
              </Link>
            )}
          </div>
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsAppClick({ placement: "catalog_header" })}
            className="inline-flex self-start rounded-full px-5 md:px-6 py-3 bg-[#25D366] text-black font-medium text-sm hover:brightness-110 transition"
            data-testid="catalog-whatsapp-cta"
          >
            Chat on WhatsApp
          </a>
        </div>

        <div className="mt-14">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="glass rounded-3xl aspect-[4/5] shimmer" />
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {list.map((p, idx) => (
                <ProductCard key={p.id} p={p} idx={idx} isFallback={usingFallback} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

const ProductCard = ({ p, idx, isFallback }) => {
  const enquire = () => {
    trackWhatsAppClick({ placement: "catalog_card", productId: p.id });
    const msg = `Hi Shine on Coin, I'm interested in *${p.name}* from your catalog. Could you share details?`;
    window.open(`${CONTACT.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const inner = (
    <>
      <div className="relative aspect-[4/5] overflow-hidden bg-black">
        {p.image_url ? (
          <img
            src={p.image_url}
            alt={p.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30 font-display text-5xl">
            {p.metal === "silver" ? "○" : "◉"}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        <div className="absolute top-4 left-4 text-[10px] tracking-widest-2 uppercase text-white/80 glass px-3 py-1 rounded-full">
          {p.category}
        </div>
        <div className="absolute top-4 right-4 w-10 h-10 rounded-full glass flex items-center justify-center border border-white/10 group-hover:bg-[#d4af37] group-hover:text-black transition">
          <ArrowUpRight size={18} />
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-display text-xl text-white">{p.name}</div>
            <div className="text-xs uppercase tracking-widest-2 text-white/50 mt-1">
              {p._designLabel || p.metal}
            </div>
          </div>
          <div className="text-xs text-[#d4af37] whitespace-nowrap">{p.price_hint}</div>
        </div>
        {p.description && (
          <p className="mt-3 text-sm text-white/60 line-clamp-2">{p.description}</p>
        )}
        <div className="mt-4 text-xs tracking-widest-2 uppercase text-white/60 group-hover:text-[#d4af37] transition">
          {isFallback ? "View All Similar →" : "Enquire on WhatsApp →"}
        </div>
      </div>
    </>
  );

  if (isFallback && p._samplesHref) {
    return (
      <Link
        to={p._samplesHref}
        className={`group text-left glass glass-hover rounded-3xl overflow-hidden fade-up fade-up-delay-${Math.min(idx, 3)} block`}
        data-testid={`catalog-product-${p.id}`}
      >
        {inner}
      </Link>
    );
  }

  return (
    <button
      onClick={enquire}
      className={`group text-left glass glass-hover rounded-3xl overflow-hidden fade-up fade-up-delay-${Math.min(idx, 3)}`}
      data-testid={`catalog-product-${p.id}`}
    >
      {inner}
    </button>
  );
};
